"use client";

import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import CourseCard from "@/components/ui/CourseCard";
import SearchSection from "@/components/ui/SearchSection";
import { LoadMore } from "@/components/ui/Pagination";
import { createClient } from "@/lib/supabase/client";
import { isAuthLockFailure } from "@/lib/supabase/authLock";
import { Tables } from "@/types/database";
import { buildCursorQuery, getTotalCount, getPageInfo } from "@/lib/utils/pagination";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useAuth } from "@/lib/context/AuthContext";
import { useSpecialties } from "@/lib/swr";
import Link from "next/link";
import { Button } from "@/components/ui/button";

type Content = Tables<'content'>;
type Ticket = Tables<'tickets'>;
type SupabaseClient = ReturnType<typeof createClient>;
type QueryBuilder = ReturnType<SupabaseClient['from']>;

interface ContentWithTickets extends Content {
  tickets?: Ticket[];
}

interface EnrollmentDetails {
  content_id: string;
  completed_at: string | null;
  progress_percentage: number | null;
}

interface BrowseContentProps {
  onRegister: (courseId: string, courseTitle?: string, courseType?: 'course' | 'event') => void;
  variant?: 'page' | 'section';
  specialty?: string;
}

export default function BrowseContent({ onRegister, variant = 'page', specialty: initialSpecialty }: BrowseContentProps) {
  const { user } = useAuth();
  const { data: specialtyRows } = useSpecialties();
  const [courses, setCourses] = useState<ContentWithTickets[]>([]);
  const [enrolledCourseIds, setEnrolledCourseIds] = useState<Set<string>>(new Set());
  const [enrollmentDetails, setEnrollmentDetails] = useState<Map<string, EnrollmentDetails>>(new Map());
  const [loading, setLoading] = useState(true);
  const [activeFilter] = useState<string>('all');
  const [totalCourses, setTotalCourses] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<{ orderValue: unknown; id: string } | null>(null);
  const [searchPerformed, setSearchPerformed] = useState(false);
  const [includePast, setIncludePast] = useState(false);
  const [showingPastFallback, setShowingPastFallback] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const supabase = useMemo(() => createClient(), []);
  const { t } = useLanguage();
  const currentFetchRef = useRef<AbortController | null>(null);

  const [currentFilters, setCurrentFilters] = useState({
    query: '',
    specialty: initialSpecialty ?? 'all',
    type: 'all',
  });

  const COURSES_PER_PAGE = 12;

  const fetchCourses = useCallback(async (
    query = '',
    specialty = '',
    type = 'all',
    cursor: { orderValue: unknown; id: string } | null = null,
    append = false,
    pastEvents = false
  ): Promise<number | null> => {
    if (!append && currentFetchRef.current) {
      currentFetchRef.current.abort();
    }

    const fetchController = new AbortController();
    if (!append) {
      currentFetchRef.current = fetchController;
    }

    const signal = fetchController.signal;

    try {
      setLoading(true);
      setLoadError(false);

      let specialtyName: string | null = null;
      if (specialty && specialty !== 'all') {
        specialtyName = specialtyRows?.find(row => row.id === specialty)?.mozLeaf ?? null;
      }

      const applyFilters = (q: QueryBuilder) => {
        let filtered = q
          .eq('status', 'published')
          .not('is_private', 'is', true)
          .is('cancelled_at', null);

        if (type && type !== 'all') {
          filtered = filtered.eq('type', type as 'course' | 'event');
        }

        if (query && query.trim()) {
          filtered = filtered.or(`title.ilike.%${query}%,description.ilike.%${query}%`);
        }

        if (specialtyName) {
          // target_specialties is jsonb, stored as [{ name, path }, ...].
          // `.filter('target_specialties::text', 'ilike', ...)` lost the cast
          // on the way to Postgres and raised
          // 42883 operator does not exist: jsonb ~~* unknown - the filter
          // matched nothing and the error was swallowed.
          //
          // `.contains()` is not usable here: postgrest-js treats any JS
          // array as a Postgres array literal (`cs.{a,b,c}`, via
          // `value.join(',')`), not JSON, so passing the jsonb shape
          // `[{ name: specialtyName }]` serializes each element via
          // toString() into `cs.{[object Object]}` and Postgres 400s. Using
          // the `cs` (contains) operator through `.filter()` keeps the value
          // as real JSON instead.
          filtered = filtered.filter('target_specialties', 'cs', JSON.stringify([{ name: specialtyName }]));
        }

        if (!pastEvents) {
          const now = new Date().toISOString();

          if (type === 'event') {
            filtered = filtered.or(`end_date.is.null,end_date.gte.${now}`);
          } else if (type === 'all' || !type) {
            filtered = filtered.or(`type.eq.course,and(type.eq.event,or(end_date.is.null,end_date.gte.${now}))`);
          }
        }

        return filtered;
      };

      if (!cursor && totalCourses === null) {
        const count = await getTotalCount({
          supabase,
          table: 'content',
          filters: applyFilters,
          useEstimate: true,
        });
        setTotalCourses(count);
      }

      const sortBy = pastEvents ? 'start_date' : 'created_at';

      const paginationQuery = buildCursorQuery({
        supabase,
        table: 'content',
        select: `
          id, title, type, status, image_url, start_date, end_date, created_at,
          cancelled_at, event_format, organizer_id, is_private,
          organizers:organizer_profiles (id, name, image_url),
          tickets (
            id,
            name,
            price,
            currency,
            status,
            max_quantity,
            sold_quantity,
            requires_promocode
          )
        `,
        orderBy: sortBy,
        orderDirection: 'desc',
        pageSize: COURSES_PER_PAGE,
        cursor,
        filters: applyFilters,
      });

      const { data, error } = await paginationQuery;

      if (signal.aborted) {
        setLoading(false);
        return null;
      }

      if (error) {
        console.error('[Browse] Error fetching courses:', error);

        // Detect auth-related PostgREST errors (expired/corrupted JWT).
        // When the Supabase client sends a stale token, PostgREST returns 401
        // for ALL queries – even public ones. Dispatch recovery so AuthContext
        // can clear the zombie session and public queries start working again.
        //
        // An auth-lock timeout is not one of them: the request never left the
        // device and the session is fine, so clearing cookies would only log
        // the user out (2026-09-17). Its error text contains the lock name
        // ("…-auth-token"), so it must be ruled out before the "token" check.
        const msg = error.message?.toLowerCase() || '';
        const code = (error as { code?: string }).code || '';
        if (isAuthLockFailure(error)) {
          console.warn('[Browse] Auth lock unavailable – showing retry');
        } else if (code === 'PGRST301' || msg.includes('jwt') || msg.includes('token') || msg.includes('unauthorized')) {
          console.warn('[Browse] Auth query error – triggering session recovery');
          window.dispatchEvent(new CustomEvent('auth:session-restore-failed'));
        }

        // Render a visible error state instead of falling through to the
        // "Нічого не знайдено" empty state – a failed fetch is not "no events".
        if (!append) setLoadError(true);
        setLoading(false);
        return null;
      }

      const results = (data || []) as unknown as ContentWithTickets[];
      const pageInfo = getPageInfo(results as unknown as { id: string;[key: string]: unknown }[], COURSES_PER_PAGE, sortBy);
      const pageData = pageInfo.hasMore ? results.slice(0, COURSES_PER_PAGE) : results;

      if (append) {
        setCourses(prev => [...prev, ...pageData]);
      } else {
        setCourses(pageData);
      }

      setHasMore(pageInfo.hasMore);
      setNextCursor(pageInfo.nextCursor);

      if (user && pageData.length > 0) {
        const courseIds = pageData.map((course: ContentWithTickets) => course.id);
        const { data: enrollments } = await supabase
          .from('enrollments')
          .select('content_id, completed_at, progress_percentage')
          .eq('user_id', user.id)
          .in('content_id', courseIds);

        if (signal.aborted) {
          setLoading(false);
          return null;
        }

        if (enrollments) {
          setEnrolledCourseIds(prev => {
            const newSet = new Set(prev);
            enrollments.forEach(e => newSet.add(e.content_id));
            return newSet;
          });

          setEnrollmentDetails(prev => {
            const newMap = new Map(prev);
            enrollments.forEach(e => {
              newMap.set(e.content_id, {
                content_id: e.content_id,
                completed_at: e.completed_at,
                progress_percentage: e.progress_percentage,
              });
            });
            return newMap;
          });
        }
      }

      return pageData.length;
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('Error fetching courses:', error);
        if (!append) setLoadError(true);
      }
      return null;
    } finally {
      if (!append && currentFetchRef.current === fetchController) {
        currentFetchRef.current = null;
      }
      setLoading(false);
    }
  }, [supabase, user, totalCourses, specialtyRows]);

  // Default view (no search): if there are no upcoming items, fall back to
  // past ones so the page doesn't look empty. `specialtyOverride` lets callers
  // that already know they want no specialty filter (e.g. "clear filters")
  // force it, instead of reading the (possibly stale, pre-reset) state.
  const fetchWithPastFallback = useCallback(async (specialtyOverride?: string) => {
    const initialSpecialty = specialtyOverride !== undefined
      ? specialtyOverride
      : (currentFilters.specialty !== 'all' ? currentFilters.specialty : '');
    const count = await fetchCourses('', initialSpecialty, 'all', null, false, false);
    if (count === 0) {
      setIncludePast(true);
      setShowingPastFallback(true);
      setTotalCourses(null);
      fetchCourses('', initialSpecialty, 'all', null, false, true);
    }
  }, [fetchCourses, currentFilters.specialty]);

  const handleLoadMore = useCallback(() => {
    if (!hasMore || loading) return;

    fetchCourses(
      currentFilters.query,
      currentFilters.specialty,
      currentFilters.type,
      nextCursor,
      true,
      includePast
    );
  }, [hasMore, loading, currentFilters, nextCursor, fetchCourses, includePast]);

  useEffect(() => {
    // An initial specialty (deep-link) resolves its display name from the
    // catalog fetched by useSpecialties, which loads asynchronously. Wait for
    // it so the very first fetch doesn't silently drop the filter.
    if (currentFilters.specialty !== 'all' && specialtyRows === null) {
      return;
    }
    fetchWithPastFallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specialtyRows]);

  const userId = user?.id;
  useEffect(() => {
    if (userId && !loading && courses.length > 0) {
      const courseIds = courses.map(course => course.id);

      const fetchEnrollments = async () => {
        try {
          const { data: enrollments } = await supabase
            .from('enrollments')
            .select('content_id, completed_at, progress_percentage')
            .eq('user_id', userId)
            .in('content_id', courseIds);

          if (enrollments) {
            setEnrolledCourseIds(prev => {
              const newSet = new Set(prev);
              enrollments.forEach(e => newSet.add(e.content_id));
              return newSet;
            });

            setEnrollmentDetails(prev => {
              const newMap = new Map(prev);
              enrollments.forEach(e => {
                newMap.set(e.content_id, {
                  content_id: e.content_id,
                  completed_at: e.completed_at,
                  progress_percentage: e.progress_percentage,
                });
              });
              return newMap;
            });
          }
        } catch (error) {
          console.error('Error fetching enrollments on login:', error);
        }
      };

      fetchEnrollments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, loading]);

  const handleSearch = useCallback((query: string, specialty: string, type: string) => {
    setCurrentFilters({ query, specialty, type });
    setNextCursor(null);
    setTotalCourses(null);
    setCourses([]);
    setEnrolledCourseIds(new Set());
    setEnrollmentDetails(new Map());
    setSearchPerformed(true);
    setShowingPastFallback(false);
    fetchCourses(query, specialty, type, null, false, includePast);
  }, [fetchCourses, includePast]);

  const handleIncludePastChange = useCallback((value: boolean) => {
    setIncludePast(value);
    setNextCursor(null);
    setTotalCourses(null);
    setCourses([]);
    setEnrolledCourseIds(new Set());
    setEnrollmentDetails(new Map());
    setSearchPerformed(true);
    setShowingPastFallback(false);
    fetchCourses(currentFilters.query, currentFilters.specialty, currentFilters.type, null, false, value);
  }, [fetchCourses, currentFilters]);

  const handleClearSearch = () => {
    setCurrentFilters({ query: '', specialty: 'all', type: 'all' });
    setIncludePast(false);
    setSearchPerformed(false);
    setShowingPastFallback(false);
    fetchWithPastFallback('');
  };

  const handleRetry = useCallback(() => {
    fetchCourses(currentFilters.query, currentFilters.specialty, currentFilters.type, null, false, includePast);
  }, [fetchCourses, currentFilters, includePast]);

  const handleRegister = useCallback((courseId: string) => {
    const content = courses.find(c => c.id === courseId);
    onRegister(courseId, content?.title, content?.type as 'course' | 'event');
  }, [courses, onRegister]);

  const resultsContent = (
    <>
      {loading && courses.length === 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="bg-white rounded-lg border border-gray-200 overflow-hidden animate-pulse">
              <div className="h-48 bg-gray-200"></div>
              <div className="p-5">
                <div className="h-6 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded mb-4 w-3/4"></div>
                <div className="space-y-2 mb-4">
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
                <div className="h-10 bg-gray-200 rounded w-full"></div>
              </div>
            </div>
          ))}
        </div>
      ) : loadError && courses.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto mb-6 flex items-center justify-center">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('home.browse.loadFailed')}</h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">{t('home.browse.loadFailedDesc')}</p>
          <Button onClick={handleRetry}>
            {t('home.browse.retry')}
          </Button>
        </div>
      ) : courses.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-20 h-20 bg-gray-100 rounded-full mx-auto mb-6 flex items-center justify-center">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('home.browse.noResults')}</h3>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">{t('home.browse.noResultsDesc')}</p>

          {searchPerformed && (
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button onClick={handleClearSearch}>
                {t('home.browse.viewAll')}
              </Button>
              {variant === 'page' && (
                <Link href="/">
                  <Button variant="secondary">
                    {t('home.browse.backToHome')}
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          {showingPastFallback && (
            <p className="text-sm text-blue-800 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 mb-6">
              {t('home.browse.showingPastFallback')}
            </p>
          )}

          {totalCourses !== null && (
            <p className="text-sm text-gray-600 mb-6">
              {t('home.browse.resultsCount').replace('{{count}}', totalCourses.toString())}
            </p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {courses.map((course) => {
              const enrollment = enrollmentDetails.get(course.id);
              return (
                <CourseCard
                  key={course.id}
                  course={course}
                  onRegister={handleRegister}
                  isEnrolled={enrolledCourseIds.has(course.id)}
                  completedAt={enrollment?.completed_at || null}
                  progressPercentage={enrollment?.progress_percentage || null}
                />
              );
            })}
          </div>

          {hasMore ? (
            <LoadMore
              hasMore={hasMore}
              onLoadMore={handleLoadMore}
              isLoading={loading}
              className="mt-8"
              labels={{
                loadMore: t('home.courses.loadMore') || 'Load More',
                loading: t('common.loading') || 'Loading...',
                noMore: t('home.courses.noMore') || 'No more items',
              }}
            />
          ) : !includePast ? (
            <div className="text-center mt-8">
              <button
                type="button"
                onClick={() => handleIncludePastChange(true)}
                className="text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
              >
                {t('home.search.showPastEvents')}
              </button>
            </div>
          ) : null}
        </>
      )}
    </>
  );

  if (variant === 'section') {
    return (
      <section id="browse" className="bg-gray-50/50 py-12 sm:py-16 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto mb-8 sm:mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 text-center">
              {t('home.browseSection.title')}
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto text-center mb-8">
              {t('home.browseSection.subtitle')}
            </p>
            <SearchSection
              onSearch={handleSearch}
              activeFilter={activeFilter}
              includePast={includePast}
              onIncludePastChange={handleIncludePastChange}
            />
          </div>
          {resultsContent}
        </div>
      </section>
    );
  }

  return (
    <>
      {/* Hero Section with Search */}
      <section className="bg-gradient-to-b from-blue-50/70 via-blue-50/30 to-white py-12 sm:py-14 md:py-16 lg:py-20 border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
              {t('home.browse.title')}
            </h1>
            <p className="text-base sm:text-lg text-gray-600 mb-8 max-w-2xl mx-auto text-center">
              {t('home.browse.subtitle')}
            </p>
            <SearchSection
              onSearch={handleSearch}
              activeFilter={activeFilter}
              includePast={includePast}
              onIncludePastChange={handleIncludePastChange}
            />
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {resultsContent}
      </section>
    </>
  );
}
